import axios from "axios";
import { Loader2, Plus } from "lucide-react";
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import EditorTextArea from "../../../../../components/EditorTextArea";
import { Button } from "../../../../../components/ui/button";
import { Card, CardContent } from "../../../../../components/ui/card";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "../../../../../components/ui/drawer";
import { Input } from "../../../../../components/ui/input";
import { useLanguage } from "../../../../../context/LanguageContext";
import { BaseUrlApi, ErrorMessage } from "../../../../../lib/api";
import { SubLessonData } from "../../../../../lib/languages";
import MainButton from "../../../../../components/MainButton";
import { useParams } from "react-router-dom";
import MultiFileUpload from "../../../../../components/MultiFileUpload";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../../components/ui/select";
import { useAPI } from "../../../../../context/APIContext";

const DrawerAddHomeWork = ({ fetchAssignments, isTeacher }) => {
  const { id } = useParams();
  const { language } = useLanguage();
  const t = SubLessonData[language] || SubLessonData["en"];

  // Get All Lessons
  const [loadingLessons, setLoadingLessons] = useState(true);
  const [lessons, setLessons] = useState([]);
  const AllLessons = () => {
    axios
      .get(`${BaseUrlApi}/teacher/lessons`)
      .then(({ data }) => {
        setLessons(data.data);
      })
      .catch((error) => {
        toast("Error loading lessons", {
          description: ErrorMessage(error),
        });
      })
      .finally(() => setLoadingLessons(false));
  };

  useEffect(() => {
    AllLessons();
  }, []);

  // Home work States
  const [open, setOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [form, setForm] = useState({
    title_en: "",
    title_ar: "",
    description_en: "",
    description_ar: "",
    score: 0,
    max_items: 0,
    due_date: "",
    lesson_id: 0,
  });

  const handleAddHomeWork = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const formData = new FormData();
      if (form.title_ar || form.title_en) {
        formData.append(
          "title",
          JSON.stringify({ ar: form.title_ar, en: form.title_en })
        );
      }
      if (form.description_ar || form.description_en) {
        formData.append(
          "description",
          JSON.stringify({ ar: form.description_ar, en: form.description_en })
        );
      }
      formData.append("files", JSON.stringify(files));
      formData.append("score", form.score);
      formData.append("max_items", form.max_items);
      formData.append("due_date", form.due_date);

      await axios.post(
        `${BaseUrlApi}/lesson/${isTeacher ? form.lesson_id : id}/assignments`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      toast(t.addHomeWork, {
        description: "The new home work has been successfully added 🎉",
      });

      await fetchAssignments();
      setOpen(false);
      ClearAll();
    } catch (error) {
      setErrors(ErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const ClearAll = () => {
    setErrors({});
    setForm({
      title_en: "",
      title_ar: "",
      description_en: "",
      description_ar: "",
      files: [],
      score: 0,
      max_items: 0,
      due_date: "",
    });
  };

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild>
        <div className="text-end">
          <MainButton Icon={Plus} title={t.addHomeWork} />
        </div>
      </DrawerTrigger>
      <DrawerContent dir={language === "ar" ? "rtl" : "ltr"}>
        <div className="h-[calc(100vh-2rem)] max-w-7xl w-full mx-auto overflow-auto hide-scrollbar space-y-4 p-4">
          {/* Header */}
          <DrawerHeader className="flex items-center justify-between flex-wrap gap-4 p-0 pb-2">
            <div className="w-full sm:w-fit">
              <DrawerTitle className="font-bold text-2xl">
                {t.addHomeWork}
              </DrawerTitle>
              <DrawerDescription className="font-medium text-sm">
                {t.addHomeWorkDescription}
              </DrawerDescription>
            </div>
            <div className="flex items-center gap-2 w-full sm:w-fit">
              <Button
                className="w-full sm:w-fit h-10 bg-blue-600 text-white hover:bg-blue-600/80 rounded-xl z-10"
                onClick={handleAddHomeWork}
                disabled={loading}
              >
                {loading && (
                  <div className="animate-spin">
                    <Loader2 />
                  </div>
                )}
                {t.addHomeWorkButton}
              </Button>
              <DrawerClose asChild>
                <Button
                  variant="outline"
                  className="w-full sm:w-fit h-10 border-2 border-blue-600 text-blue-600 hover:text-blue-500 rounded-xl"
                >
                  {t.cancel}
                </Button>
              </DrawerClose>
            </div>
          </DrawerHeader>
          {/* Uploads */}
          <div className="grid grid-cols-1 md:grid-cols-1 gap-4 relative">
            <Card>
              <CardContent className="p-4 space-y-4 flex flex-col h-full">
                <p className="font-bold text-xl flex items-center gap-2">
                  {t.files}
                </p>
                <MultiFileUpload
                  onUploadComplete={(files) => setFiles(files)}
                  name={"home-work"}
                />
              </CardContent>
            </Card>
          </div>
          {/* General Information */}
          <Card>
            <CardContent className="p-4 space-y-4 flex-1 flex flex-col">
              <p className="font-bold text-xl border-b pb-2">
                {t.generalInformation}
              </p>
              <div className="space-y-4 flex-1 flex flex-col">
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2 w-full">
                    <label htmlFor="title_en" className="text-sm">
                      {t.titleEn}
                    </label>
                    <Input
                      id="title_en"
                      name="title_en"
                      type="text"
                      placeholder={t.enterTitleEn}
                      onChange={(e) =>
                        setForm({ ...form, title_en: e.target.value })
                      }
                    />
                    {errors?.title && (
                      <p className="text-red-500 font-semibold text-sm">
                        {errors.title[0]}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 w-full">
                    <label htmlFor="title_ar" className="text-sm">
                      {t.titleAr}
                    </label>
                    <Input
                      id="title_ar"
                      name="title_ar"
                      type="text"
                      placeholder={t.enterTitleAr}
                      onChange={(e) =>
                        setForm({ ...form, title_ar: e.target.value })
                      }
                    />
                    {errors?.title && (
                      <p className="text-red-500 font-semibold text-sm">
                        {errors.title[0]}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 w-full">
                    <label htmlFor="score" className="text-sm">
                      {t.score}
                    </label>
                    <Input
                      id="score"
                      name="score"
                      type="number"
                      placeholder={"Enter score of home work"}
                      onChange={(e) =>
                        setForm({ ...form, score: e.target.value })
                      }
                      value={form.score}
                    />
                    {errors?.score && (
                      <p className="text-red-500 font-semibold text-sm">
                        {errors.score[0]}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 w-full">
                    <label htmlFor="max_items" className="text-sm">
                      {t.maxItems}
                    </label>
                    <Input
                      id="max_items"
                      name="max_items"
                      type="number"
                      placeholder={"Enter number of files can student upload"}
                      onChange={(e) =>
                        setForm({ ...form, max_items: e.target.value })
                      }
                      value={form.max_items}
                    />
                    {errors?.max_items && (
                      <p className="text-red-500 font-semibold text-sm">
                        {errors.max_items[0]}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 w-full">
                    <label htmlFor="due_date" className="text-sm">
                      {t.expireDate}
                    </label>
                    <DatePicker
                      onChange={(value, dateString) =>
                        setForm({ ...form, due_date: dateString })
                      }
                      defaultValue={form.due_date && dayjs(form.due_date)}
                    />
                    {errors?.due_date && (
                      <p className="text-red-500 font-semibold text-sm">
                        {errors.due_date[0]}
                      </p>
                    )}
                  </div>
                  {isTeacher && (
                    <div className="flex flex-col gap-2 w-full">
                      <label htmlFor="due_date" className="text-sm">
                        {t.selectLesson}
                      </label>
                      <Select
                        value={form.lesson_id || ""}
                        onValueChange={(value) =>
                          setForm({ ...form, lesson_id: +value })
                        }
                        className="w-full p-2 border rounded"
                        disabled={loadingLessons}
                      >
                        <SelectTrigger
                          className="rounded-lg"
                          aria-label="Select Lesson"
                        >
                          <SelectValue placeholder="Select Lesson" />
                        </SelectTrigger>
                        <SelectContent>
                          {lessons.length !== 0 ? (
                            lessons.map((lesson) => (
                              <SelectItem key={lesson} value={lesson.id}>
                                {lesson.title[language] ||
                                  lesson.title.en ||
                                  lesson.title.ar}
                              </SelectItem>
                            ))
                          ) : (
                            <SelectItem>No lessons found</SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                      {errors?.lesson_id && (
                        <p className="text-red-500 font-semibold text-sm">
                          {errors.lesson_id[0]}
                        </p>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-2 flex-1">
                  <label htmlFor="description" className="text-sm">
                    {t.descriptionLabel}
                  </label>
                  <EditorTextArea
                    form={form}
                    en="description_en"
                    ar="description_ar"
                    setForm={setForm}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default DrawerAddHomeWork;
